/**
 * A child's own card, over the valley: the display's resident card, ported.
 *
 * Tapping a child in the valley opens it from the right -- their character at
 * full height, their name, their money, and their year, grouped by month.
 * Nothing on it can change anything; it only answers the two questions a
 * child asks of the wall: how much have I got, and what did I do to get it.
 * (apps/display/public/scene.js `residentCard`/`residentJournal`, and the
 * `.resident-card` rules in scene.css.)
 *
 * Two lines the display did not have, because the money rules they describe
 * did not exist there: what is saved in goals, and when the next allowance is.
 *
 * It closes by itself after a minute untouched. A card left open on a wall
 * all afternoon is a card nobody meant to leave open.
 */

import { Loading } from '../components/Spinner';
import { useEffect, useMemo, useRef, useState } from 'react';
import { loadJournal } from '../family/journal';
import { dollars } from '../family/money';
import type { AllowancePlan, Entry, Person } from '../family/state';
import { render } from '../avatar/painted';
import { currentUnit, unitNoun } from '../family/money';
import { MarbleJar } from './MarbleJar';

const IDLE_MS = 60_000;
const OFTEN = { week: 'a week', fortnight: 'every two weeks', month: 'a month' } as const;

export function ResidentCard({
  familyId,
  familyKey,
  person,
  setAside,
  allowance,
  onClose,
}: {
  familyId: string;
  familyKey: CryptoKey;
  person: Person;
  /** How much of their money is in goals. */
  setAside: number;
  allowance: AllowancePlan | null;
  onClose: () => void;
}) {
  const [rows, setRows] = useState<Entry[] | null>(null);
  const [failed, setFailed] = useState(false);
  const close = useRef<HTMLButtonElement>(null);
  const shut = useRef(onClose);
  shut.current = onClose;

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setFailed(false);
    void loadJournal(familyId, familyKey, { person: person.id })
      .then((journal) => {
        if (!cancelled) setRows(journal.entries);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    close.current?.focus();
    return () => {
      cancelled = true;
    };
  }, [familyId, familyKey, person.id]);

  // Escape, and a minute with nobody touching it.
  useEffect(() => {
    let timer = window.setTimeout(() => shut.current(), IDLE_MS);
    const poke = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => shut.current(), IDLE_MS);
    };
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') shut.current();
      else poke();
    };
    window.addEventListener('keydown', key);
    window.addEventListener('pointerdown', poke);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('keydown', key);
      window.removeEventListener('pointerdown', poke);
    };
  }, []);

  // A painted SVG string from small integers; see avatar/Character.tsx.
  const figure = useMemo(
    // The standing portrait is painted in the first outfit only.
    () => render({ ...person.avatar, outfit: 0 }, { size: 200, activity: 'standing', key: `card-${person.id}`, title: person.name }),
    [person.avatar, person.id, person.name],
  );
  const balance = person.held;

  return (
    <aside className="resident-card" aria-label={`${person.name || 'Their'} card`}>
      <button type="button" className="card-close" ref={close} aria-label="Close" onClick={onClose}>
        ×
      </button>
      <div className="card-top">
        {/* eslint-disable-next-line react/no-danger -- see above */}
        <div className="card-figure" dangerouslySetInnerHTML={{ __html: figure }} />
        <div className="card-who">
          <h2>{person.name}</h2>
          <div className={balance < 0 ? 'card-money debit' : 'card-money'}>{dollars(balance)}</div>
          <p>their own {unitNoun()}</p>
          {currentUnit().type === 'marbles' ? (
            <div className="card-jar">
              <MarbleJar count={Math.max(0, Math.floor(balance / 100))} size={84} />
            </div>
          ) : null}
          {setAside > 0 ? <p>in goals: {dollars(setAside)}</p> : null}
          {allowance && !allowance.paused ? (
            <p>
              {dollars(allowance.amount)} allowance {OFTEN[allowance.every]} &middot; next{' '}
              {new Date(`${allowance.next}T12:00:00`).toLocaleDateString(undefined, {
                weekday: 'short',
                day: 'numeric',
                month: 'short',
              })}
            </p>
          ) : null}
        </div>
      </div>
      <div className="card-scroll">
        {failed ? (
          <p className="card-note">The journal could not be read just now.</p>
        ) : rows === null ? (
          <Loading label="Reading their journal…" />
        ) : (
          <Year rows={rows} personId={person.id} />
        )}
      </div>
    </aside>
  );
}

const monthName = (value: string) =>
  new Date(`${value}-01T12:00:00`).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
const signed = (cents: number) => `${cents < 0 ? '−' : '+'}${dollars(Math.abs(cents))}`;

/** `residentJournal`: what counts, by year and month, newest first, their share only. */
function Year({ rows, personId }: { rows: Entry[]; personId: string }) {
  const mine = (row: Entry) => row.shares[personId] ?? 0;
  const years = new Map<string, Map<string, Entry[]>>();
  for (const row of rows) {
    if (row.voided) continue; // a child is shown what counts
    if (!(personId in row.shares)) continue; // and only their own rows
    const year = row.date.slice(0, 4);
    const month = row.date.slice(0, 7);
    if (!years.has(year)) years.set(year, new Map());
    const months = years.get(year)!;
    if (!months.has(month)) months.set(month, []);
    months.get(month)!.push(row);
  }
  if (!years.size) {
    return <p className="card-note">Nothing in the journal yet. That changes the first time you help out.</p>;
  }
  const sum = (list: Entry[]) => list.reduce((total, row) => total + mine(row), 0);
  return (
    <>
      {[...years].map(([year, months], index) => (
        <details className="card-year" key={year} open={index === 0}>
          <summary>
            <b>{year}</b>
            <i>{signed(sum([...months.values()].flat()))}</i>
          </summary>
          {[...months].map(([month, list]) => (
            <div className="card-month" key={month}>
              <h3>
                {monthName(month)}
                <i>{signed(sum(list))}</i>
              </h3>
              {list.map((row) => (
                <div className={mine(row) < 0 ? 'card-entry debit' : 'card-entry'} key={row.id}>
                  <span>{row.reason || 'A grown-up added this'}</span>
                  <b>{signed(mine(row))}</b>
                </div>
              ))}
            </div>
          ))}
        </details>
      ))}
    </>
  );
}
